import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  Divider,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon 
} from '@mui/icons-material';
import { fetchCarePlanBudget } from '../services/healinApi';

const CarePlanBudgetTest = () => {
  const [profileId, setProfileId] = useState('eu-west-1:804eaaf3-93ef-c40e-446a-d8b6ad3cdd63');
  const [carePlanBudget, setCarePlanBudget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFetchCarePlan = async () => {
    if (!profileId.trim()) {
      setError('Please enter a Profile ID');
      return;
    }

    setLoading(true);
    setError(null);
    setCarePlanBudget(null);

    try {
      const data = await fetchCarePlanBudget(profileId.trim());
      setCarePlanBudget(data);
      console.log("Care plan data:", data);
    } catch (err) {
      console.error('Error fetching care plan budget:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  // Format currency amount
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    if (amount === '') return '€0.00';
    
    try {
      // Handle European format (comma as decimal separator)
      if (typeof amount === 'string' && amount.includes(',')) {
        amount = amount.replace(',', '.');
      }
      return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR'
      }).format(parseFloat(amount));
    } catch (e) {
      return amount;
    }
  };

  // Get procedures list from carePlanBudget
  const getScheduledProcedures = (data) => {
    if (!data) return [];
    return data.ScheduledProcedures || [];
  };

  // Normalize category names to handle variations and typos
  const normalizeCategory = (category) => {
    if (!category) return 'Other';
    
    // Convert to lowercase for case-insensitive comparison
    const lowerCategory = category.toLowerCase();
    
    // Handle variations of Laboratório - be more specific
    if (lowerCategory.includes('laboratório') || 
        lowerCategory.includes('análises') || 
        lowerCategory === 'lab' || 
        lowerCategory === 'análises clínicas') {
      return 'Laboratório';
    }
    
    // Handle variations of Medicamento
    if (lowerCategory.includes('medicamento') || 
        lowerCategory.includes('fármaco') || 
        lowerCategory === 'med') {
      return 'Medicamento';
    }
    
    // Handle variations of Consulta
    if (lowerCategory.includes('consulta') || 
        lowerCategory === 'visita' || 
        lowerCategory === 'atendimento') {
      return 'Consulta';
    }
    
    // Exams category
    if (lowerCategory.includes('exame') || 
        lowerCategory.includes('imagiologia') || 
        lowerCategory.includes('radiologia')) {
      return 'Exames Complementares';
    }
    
    // Return the original if no matches
    return category;
  };

  // Group procedures by class
  const getClassGroups = (procedures) => {
    if (!procedures || !Array.isArray(procedures)) return {};

    // Group items by normalized class
    const classGroups = {};
    procedures.forEach(procedure => {
      const className = normalizeCategory(procedure.Class || 'Other');
      if (!classGroups[className]) {
        classGroups[className] = [];
      }
      classGroups[className].push(procedure);
    });

    return classGroups;
  };

  // Get activated pathways
  const getActivatedPathways = (data) => {
    if (!data || !data.Pathways || !data.Pathways.pathways) return [];
    
    // Return the pathways with mapped property names
    return data.Pathways.pathways.map(pathway => ({
      name: pathway.patologia_de_base || 'Unnamed Pathway',
      id: pathway.ID_Pathway,
      icd10: pathway.ICD10,
      isActive: true, // We don't have active status in the API, assume all are active
      procedureCount: getPathwayProcedureCount(data, pathway.ID_Pathway)
    }));
  };

  // Count procedures associated with a pathway
  const getPathwayProcedureCount = (data, pathwayId) => {
    if (!data || !data.ScheduledProcedures) return 0;
    
    // Count procedures that might be associated with this pathway
    // This is a placeholder since we don't have direct association in the data
    return data.ScheduledProcedures.length > 0 ? 
      Math.floor(data.ScheduledProcedures.length / (data.Pathways?.pathways?.length || 1)) : 0;
  };

  // Calculate total budget
  const calculateTotalBudget = (items) => {
    if (!items || !Array.isArray(items)) return 0;

    return items.reduce((total, item) => {
      if (!item.Price || item.Price === '') return total;
      if (typeof item.Price === 'string' && item.Price.includes(',')) {
        item.Price = item.Price.replace(',', '.');
      }
      
      const price = parseFloat(item.Price) || 0;
      const number = parseInt(item.Number) || 0;
      return total + (price * number);
    }, 0);
  };

  const procedures = carePlanBudget ? getScheduledProcedures(carePlanBudget) : [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Care Plan Budget API Test
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Test the connection to Healin Care Plan Budget API.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Profile ID"
              value={profileId}
              onChange={(e) => setProfileId(e.target.value)}
              variant="outlined"
              placeholder="Enter Profile ID"
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="contained" 
              onClick={handleFetchCarePlan} 
              disabled={loading || !profileId.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Fetch Care Plan Budget'}
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {carePlanBudget && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Pathways" />
            <Tab label="Scheduled Procedures" />
            <Tab label="By Category" />
            <Tab label="Raw Data" />
          </Tabs>
          
          {tabValue === 0 && (
            <Grid container spacing={3}>
              {/* Activated Pathways */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Care Pathways
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      These are the care pathways for this patient.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {carePlanBudget.Pathways && carePlanBudget.Pathways.pathways && carePlanBudget.Pathways.pathways.length > 0 ? (
                      <List>
                        {getActivatedPathways(carePlanBudget).map((pathway, index) => {
                          return (
                            <ListItem 
                              key={index}
                              secondaryAction={
                                <Chip 
                                  label={pathway.id}
                                  color="primary"
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                              }
                            >
                              <ListItemIcon>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={pathway.name} 
                                secondary={`ICD10: ${pathway.icd10 || 'N/A'}`}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    ) : (
                      <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No active care pathways found for this patient.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              {procedures && procedures.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table sx={{ minWidth: 650 }} size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Procedure</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Time Windows</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {procedures.map((item, index) => {
                        const price = item.Price ? parseFloat(item.Price.replace(',', '.')) : 0;
                        const quantity = parseInt(item.Number) || 0;
                        const total = price * quantity;
                        const isActive = quantity > 0;
                        
                        return (
                          <TableRow
                            key={index}
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              backgroundColor: isActive ? 'rgba(46, 125, 50, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell>{item.Name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.Class || 'Other'} 
                                size="small" 
                                color={
                                  normalizeCategory(item.Class).includes('Medicamento') ? 'primary' : 
                                  normalizeCategory(item.Class).includes('Consulta') ? 'secondary' :
                                  normalizeCategory(item.Class).includes('Laboratório') ? 'success' : 'default'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">{item.Number}</TableCell>
                            <TableCell align="right">{item.Price ? formatCurrency(item.Price) : '€0.00'}</TableCell>
                            <TableCell align="right">{formatCurrency(total)}</TableCell>
                            <TableCell>
                              {item.SuggestedTimeWindows && item.SuggestedTimeWindows.map((window, idx) => (
                                <Chip 
                                  key={idx} 
                                  label={window.intervalDescription} 
                                  size="small" 
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={isActive ? 'Active' : 'Inactive'} 
                                color={isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
                  No scheduled procedures found for this patient.
                </Typography>
              )}
            </Box>
          )}

          {tabValue === 2 && (
            <Grid container spacing={3}>
              {procedures && procedures.length > 0 ? (
                Object.entries(getClassGroups(procedures)).map(([className, items], index) => (
                  <Grid item xs={12} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {className} ({items.length} items)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Procedure Name</TableCell>
                                <TableCell align="center">Quantity</TableCell>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {items.map((item, idx) => {
                                const price = item.Price ? parseFloat(item.Price.replace(',', '.')) : 0;
                                const quantity = parseInt(item.Number) || 0;
                                const total = price * quantity;
                                const isActive = quantity > 0;
                                
                                return (
                                  <TableRow 
                                    key={idx}
                                    sx={{ 
                                      backgroundColor: isActive ? 'rgba(46, 125, 50, 0.1)' : 'inherit'
                                    }}
                                  >
                                    <TableCell>{item.Name}</TableCell>
                                    <TableCell align="center">{item.Number}</TableCell>
                                    <TableCell align="right">{item.Price ? formatCurrency(item.Price) : '€0.00'}</TableCell>
                                    <TableCell align="right">{formatCurrency(total)}</TableCell>
                                    <TableCell align="center">
                                      <Chip 
                                        label={isActive ? 'Active' : 'Inactive'} 
                                        color={isActive ? 'success' : 'default'}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              <TableRow>
                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                  Subtotal:
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(calculateTotalBudget(items))}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No procedures found to categorize for this patient.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}

          {tabValue === 3 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Complete JSON Data
                </Typography>
                <Box component="pre" sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(0,0,0,0.05)', 
                  borderRadius: 1,
                  overflowX: 'auto'
                }}>
                  {JSON.stringify(carePlanBudget, null, 2)}
                </Box>
              </CardContent>
            </Card>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CarePlanBudgetTest; 